<script module>
  export const i18n = {
    en: {
      '%s× Tickets': {
        one: '%s× Ticket',
        other: '%s× Tickets'
      },
      '%s× Ancillaries': {
        one: '%s× Ancillary',
        other: '%s× Ancillaries'
      }
    },
    sv: {
      'Order summary': 'Översikt',
      '%s× Tickets': {
        one: '%s× Biljett',
        other: '%s× Biljetter'
      },
      '%s× Ancillaries': {
        one: '%s× Tillval',
        other: '%s× Tillval'
      },
      Total: 'Totalt',
      'Service fee': 'Serviceavgift',
      'Ticketing fee': 'Bokningsavgift',
      'Your trip details': 'Dina resedetaljer'
    },
    da: {
      'Order summary': 'Oversigt',
      '%s× Tickets': {
        one: '%s× Billet',
        other: '%s× Billetter'
      },
      '%s× Tillval': {
        one: '%s× Tillval',
        other: '%s× Tillval'
      },
      'Service fee': 'Servicegebyr',
      'Ticketing fee': 'Billetgebyr',
      'Your trip details': 'Dine rejseoplysninger'
    }
  }
</script>

<script>
  import { getPassengersLabel } from '$lib/utils/passengers.js'
  import { register } from '$lib/utils/text.svelte.js'
  import { money } from '$lib/utils/money.svelte.js'
  import State from '$lib/utils/state.svelte.js'
  import Loader from '$lib/Loader.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import {
    i18n as passbBundleBuilderI18n,
    getBundleTravelDays,
    getBundleValidity,
    getProductName
  } from '$lib/PassBundleBuilder.svelte'

  let text = register(i18n, passbBundleBuilderI18n)

  /** @type {{ booking: Schema.Booking, intermediate?: boolean, action?: Svelte.Snippet, message?: Svelte.Snippet }} */
  let { booking, intermediate = false, action, message } = $props()

  let passengerLabel = $derived(
    booking.passengers && getPassengersLabel(booking.passengers)
  )

  let journeySelections = /** @type {Schema.JourneyOfferSelection[]?} */ (
    $derived(
      booking.selections
        ?.filter((item) => item.__typename === 'JourneyOfferSelection')
        .sort(
          (a, b) =>
            // @ts-ignore
            a.journey.itinerary[0].segments[0].departureAt -
            // @ts-ignore
            b.journey.itinerary[0].segments[0].departureAt
        )
    )
  )

  /** @type {{ name: string, description?: string, price?: Schema.Money }[]|undefined} */
  let items = $derived(
    booking?.selections
      ?.flatMap((item) => {
        switch (item.__typename) {
          case 'JourneyOfferSelection': {
            const products = [
              {
                name: text`${item.offers.length * (booking.passengers?.length || 1)}× Tickets`,
                price: item.offers.reduce((acc, offer) => {
                  const amount = acc.amount + offer.price.amount
                  return { ...offer.price, amount }
                }, /** @type {Schema.Money} */ ({ amount: 0 }))
              }
            ]

            const ancillaries = item.offers.flatMap((offer) =>
              offer.parts
                .filter((part) => part.__typename === 'ReservationPart')
                .flatMap((/** @type {Schema.ReservationPart} */ part) =>
                  part.ancillaries?.filter(
                    (ancillary) =>
                      ancillary?.price && ancillary.selections.length
                  )
                )
                .filter(Boolean)
            )

            if (ancillaries.length) {
              const count = ancillaries.reduce((acc, ancillary) => {
                return acc + ancillary.selections.length
              }, 0)
              products.push({
                name: text`${count}× Ancillaries`,
                price: ancillaries.reduce((acc, ancillary) => {
                  const price = /** @type {Schema.Money} */ (ancillary.price)
                  const amount = price.amount * ancillary.selections.length
                  return { ...price, amount: acc.amount + amount }
                }, /** @type {Schema.Money} */ ({ amount: 0 }))
              })
            }

            return products
          }

          case 'GiftCardSelection':
            return { name: text`Gift card`, price: item.price }

          case 'PassBundleSelection':
            return {
              name: `${booking.passengers?.length}× ${getProductName(item, text)}${
                item.class ? `, ${text(item.class)}` : null
              }`,
              description: `${getBundleTravelDays(item, text)} ${getBundleValidity(
                item,
                text
              )}`,
              price: item.price
            }

          case 'AppliedGiftCard':
            return {
              name: text`Gift card`,
              price: item.value,
              description: item.code
            }

          default:
            return null
        }
      })
      .concat(
        // @ts-ignore null is filtered out below
        booking.ticketingFee
          ? {
              name: text`Service fee`,
              price: intermediate ? undefined : booking.ticketingFee
            }
          : null
      )
      .filter(Boolean)
  )

  /** @type {function(string): Date} */
  const parseDate = (value) => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
    if (!match) throw new Error('Invalid date format')
    const [, year, month, day, hour, minute] = match
    return new Date(+year, +month - 1, +day, +hour, +minute)
  }

  /** @type {function(string): string} */
  const asTimestamp = (datetime) =>
    parseDate(datetime).toLocaleTimeString(State.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
</script>

<div class="breakdown">
  <h2 class="heading">{text`Order summary`}</h2>

  {#if journeySelections?.length && booking.passengers}
    {@const segments = journeySelections
      .flatMap((selection) => selection.journey.itinerary)
      .filter((item) => item.__typename === 'SegmentCollection')
      .flatMap((/** @type {Schema.SegmentCollection} */ item) => item.segments)
      .filter(Boolean)}
    <details class="summary">
      <summary class="toggle">
        <div>
          <strong class="title">{text`Your trip details`}</strong>
          <br />
          <span class="departure">
            {new Date(segments.at(0).departureAt).toLocaleDateString(
              State.language,
              {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }
            )},
          </span>
          {#if passengerLabel}
            {booking.passengers.length}
            {text(passengerLabel)}
          {/if}
        </div>
        <div class="chevron">
          <span class="symbol">
            <Symbol name="chevron" size="sm" />
          </span>
        </div>
      </summary>
      <hr class="divider" />
      <ol class="list">
        {#each segments as segment}
          <li>
            <span class="leg">
              {segment.origin.name} – {segment.destination.name}
            </span>
            <br />
            {asTimestamp(segment.departureAt)} – {asTimestamp(
              segment.arrivalAt
            )}, {new Date(segment.departureAt).toLocaleDateString(
              State.language,
              {
                weekday: 'long',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }
            )}
          </li>
        {/each}
      </ol>
    </details>
  {/if}

  {#if items?.length}
    <hr />

    <table cellpadding="0" cellspacing="0">
      <tbody>
        {#each items as item}
          <tr>
            <td class="cell name">
              {item.name}
              {#if item.description}
                <br />
                <span class="u-textMuted">{item.description}</span>
              {/if}
            </td>
            <td class="cell value">
              {#if item.price}
                {money(item.price)}
              {:else if intermediate}
                <Loader length={2} />
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}

  <hr />

  <div class="footer">
    <p>
      {text`Total`}
      <span class="price">
        {#if booking?.totalPrice && !intermediate}
          {money(booking.totalPrice)}
        {:else}
          <Loader length={2} />
        {/if}
      </span>
    </p>
    {@render action?.()}
  </div>

  {@render message?.()}
</div>

<style>
  .breakdown {
    display: grid;
    gap: 1rem;
    width: 100%;
    font-size: 1rem;
    color: rgb(var(--rgb-text));
  }

  .heading {
    font-size: 1.75rem;
    font-weight: bold;
    font-family: var(--heading-font-family);
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
  }

  .summary {
    padding: 0.9rem 1rem;
    position: relative;
    font-size: clamp(0.875rem, 2vw, 1rem);
    letter-spacing: 0.025em;
    line-height: 1.2;
    color: rgb(var(--rgb-muted));
    background-color: var(--background-alt);
    border-radius: var(--border-radius-md);
  }

  .toggle {
    display: flex;
    align-items: flex-start;
    user-select: none;
    cursor: pointer;
  }

  .toggle::-webkit-details-marker {
    display: none;
  }

  .chevron {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    margin-left: auto;
    border-radius: 100%;
    background-color: var(--background);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
  }

  .summary:hover .chevron {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .summary:active .chevron {
    box-shadow: var(--box-shadow-sm-active);
  }

  .summary[open] .symbol {
    transform: rotate(180deg);
  }

  .title {
    color: rgb(var(--rgb-text));
  }

  .summary[open] .departure {
    display: none;
  }

  .divider {
    margin: 0.5rem 0;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .leg {
    color: rgb(var(--rgb-text));
  }

  .cell {
    padding: 0.25rem 0;
  }

  .value {
    padding-left: 1rem;
    vertical-align: baseline;
    text-align: right;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .price {
    display: block;
    font-size: clamp(1.25rem, 2vw, 1.75rem);
    line-height: 1.25;
    font-weight: bold;
  }
</style>
